import { Button } from '@televet/kibble-ui/build/components/Button';
import { Tag } from '@televet/kibble-ui/build/components/Tag';
import { ModalCloseButton } from '@televet/kibble-ui/build/components/Modal';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import {
  VStack,
  HStack,
  NumberInput,
  Wrap,
  NumberInputField,
  CModal,
  CModalBody,
  ModalContent,
  CModalFooter,
  CModalHeader,
  ModalOverlay,
  CSelect,
} from '@televet/kibble-ui/build/chakra';
import {
  ChannelStatusInactivityUnits,
  IChannelStatusChangeRule,
  IChannelStatusChangeRuleOptions,
  IStatusDisplay,
  IStatusRuleDisplay,
  mapStatusIdToStatusDisplay,
} from 'pages/Settings/pages/ConversationSettings/AutomaticStatusChanges';
import ConfirmAutomaticStatusChangeModal from 'pages/Settings/pages/ConversationSettings/ConfirmAutomaticStatusChangeModal';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import Tooltip from 'shared/components/Tooltip';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  ClinicSettingsConversationsChannelStatusFragment as ChannelStatus,
  ChannelStatusAction,
  RuleActions,
  useCreateClinicSettingsConversationsChannelStatusAutoUpdateRuleMutation,
  useUpdateClinicSettingsConversationsChannelStatusAutoUpdateRuleMutation,
} from 'shared/types/graphql';
import styled from 'styled-components/macro';

enum AdditionalInitialStatusOption {
  SelectAllActive = 'selectAllActive',
  DeselectAllActive = 'deselectAllActive',
}

interface IAddEditAutomaticStatusChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess: () => Promise<void>;
  ruleForEdit?: IStatusRuleDisplay;
  allRules: IChannelStatusChangeRule[];
  allStatuses: ChannelStatus[];
}

const AddEditAutomaticStatusChangeModal = ({
  isOpen,
  onClose,
  onSaveSuccess,
  ruleForEdit,
  allRules,
  allStatuses,
}: IAddEditAutomaticStatusChangeModalProps): ReactElement => {
  const { currentClinicId } = useClinicUser();
  const toast = useToast();

  const [createStatusRule, { loading: isSavingNewRule }] =
    useCreateClinicSettingsConversationsChannelStatusAutoUpdateRuleMutation();
  const [updateStatusRule, { loading: isSavingExistingRule }] =
    useUpdateClinicSettingsConversationsChannelStatusAutoUpdateRuleMutation();

  const isSaving = isSavingNewRule || isSavingExistingRule;
  const [timeValue, setTimeValue] = useState<number>(1);
  const [timeUnit, setTimeUnit] = useState<ChannelStatusInactivityUnits>(ChannelStatusInactivityUnits.Days);
  const [selectedInactiveStatusId, setSelectedInactiveStatusId] = useState<string | undefined>();
  const [selectedStatusIds, setSelectedStatusIds] = useState<string[]>([]);
  const [isPendingConfirm, setIsPendingConfirm] = useState<boolean>(false);

  const setFormData = useCallback(() => {
    setTimeUnit(ruleForEdit?.inactivityDurationUnit || ChannelStatusInactivityUnits.Days);
    setTimeValue(ruleForEdit?.inactivityDurationValue || 1);
    setSelectedInactiveStatusId(ruleForEdit?.inactiveStatus?.id);
    setSelectedStatusIds(ruleForEdit?.initialStatuses?.map((x) => x.id) || []);
    setIsPendingConfirm(false);
  }, [ruleForEdit, setTimeUnit, setTimeValue, setSelectedInactiveStatusId, setSelectedStatusIds, setIsPendingConfirm]);

  useEffect(() => {
    if (isOpen) {
      setFormData();
    }
  }, [isOpen, setFormData]);

  const selectableStatuses = useMemo(() => {
    return allStatuses.filter((status) => {
      return (
        !selectedStatusIds.includes(status.id) &&
        selectedInactiveStatusId !== status.id &&
        !allRules.some((rule) => rule.id !== ruleForEdit?.id && rule.options.initialStatusIds.includes(status.id))
      );
    });
  }, [allStatuses, allRules, selectedStatusIds, selectedInactiveStatusId, ruleForEdit]);

  const selectedStatuses = useMemo(() => {
    return selectedStatusIds.map<IStatusDisplay>((id) => mapStatusIdToStatusDisplay(id, allStatuses));
  }, [allStatuses, selectedStatusIds]);

  const selectableStatusesDisplay = useMemo(() => {
    return [
      { label: '', value: 'PlaceHolder' },
      { label: 'Select All Active', value: AdditionalInitialStatusOption.SelectAllActive },
      { label: 'Deselect All Active', value: AdditionalInitialStatusOption.DeselectAllActive },
      ...selectableStatuses.map((status) => ({ label: status.name, value: status.id })),
    ];
  }, [selectableStatuses]);

  const selectableInactiveStatusOptions = useMemo(() => {
    return allStatuses.filter((status) => !selectedStatusIds.includes(status.id));
  }, [allStatuses, selectedStatusIds]);

  const selectedInactiveStatus = useMemo(() => {
    if (!selectedInactiveStatusId) {
      return;
    }

    return mapStatusIdToStatusDisplay(selectedInactiveStatusId, allStatuses);
  }, [allStatuses, selectedInactiveStatusId]);

  const onTimeInputChanged = (strValue: string, value: number): void => {
    setTimeValue(value);
  };

  const onTimeInputBlur = (): void => {
    if (isNaN(timeValue)) {
      setTimeValue(1);
    }
  };

  const onTimeUnitChanged = (option: string): void => {
    const optionEnumVal =
      ChannelStatusInactivityUnits[option as keyof typeof ChannelStatusInactivityUnits] ??
      ChannelStatusInactivityUnits.Days;

    setTimeUnit(optionEnumVal);
  };

  const onAddInitialStatus = (statusId: string | undefined): void => {
    if (!statusId) {
      return;
    }

    let statusIds = [...selectedStatusIds];

    if (statusId === AdditionalInitialStatusOption.SelectAllActive) {
      statusIds = statusIds.concat(
        selectableStatuses.filter((x) => x.channelStatusAction === ChannelStatusAction.Active).map((x) => x.id),
      );
    } else if (statusId === AdditionalInitialStatusOption.DeselectAllActive) {
      statusIds = selectedStatuses.filter((x) => x.statusType !== ChannelStatusAction.Active).map((x) => x.id);
    } else {
      statusIds.push(statusId);
    }
    setSelectedStatusIds([...statusIds]);
  };

  const onRemoveInitialStatusClick = (statusId: string): void => {
    let selected = [...selectedStatusIds];
    selected = selected.filter((id) => id !== statusId);
    setSelectedStatusIds(selected);
  };

  const onInactiveStatusSelectionChanged = (statusId: string): void => {
    setSelectedInactiveStatusId(statusId);
  };

  const onSaveBtnClick = (): void => {
    setIsPendingConfirm(true);
  };

  const onConfirmBtnClick = async (): Promise<void> => {
    setIsPendingConfirm(false);
    if (!ruleForEdit) {
      await handleSaveNewRule();
    } else {
      await handleUpdateExistingRule();
    }
  };

  const onConfirmCancelBtnClick = (): void => {
    setIsPendingConfirm(false);
  };

  const handleSaveNewRule = async (): Promise<void> => {
    if (!selectedInactiveStatusId) {
      return;
    }
    const options: IChannelStatusChangeRuleOptions = {
      initialStatusIds: selectedStatusIds,
      inactiveStatusId: selectedInactiveStatusId,
      inactivityDurationValue: timeValue,
      inactivityDurationUnit: timeUnit,
    };

    try {
      await createStatusRule({
        variables: {
          data: {
            action: RuleActions.UpdateInactiveChannelStatus,
            clinic: { connect: { id: currentClinicId } },
            options: options,
          },
        },
      });
      await onSaveSuccess();
    } catch (e) {
      console.error(e);
      toast({ description: 'Something went wrong while saving the new rule. Please try again.', status: 'error' });
    }
  };

  const handleUpdateExistingRule = async (): Promise<void> => {
    if (!ruleForEdit || !selectedInactiveStatusId) {
      return;
    }

    const options: IChannelStatusChangeRuleOptions = {
      initialStatusIds: selectedStatusIds,
      inactiveStatusId: selectedInactiveStatusId,
      inactivityDurationValue: timeValue,
      inactivityDurationUnit: timeUnit,
    };

    try {
      await updateStatusRule({
        variables: {
          data: {
            options: options,
          },
          ruleId: ruleForEdit.id,
        },
      });
      await onSaveSuccess();
    } catch (e) {
      console.error(e);
      toast({ description: 'Something went wrong while saving the edited rule. Please try again.', status: 'error' });
    }
  };

  const hasMinuteError = timeUnit === ChannelStatusInactivityUnits.Minutes && (isNaN(timeValue) || timeValue < 5);
  const isSaveBtnDisabled =
    isSaving ||
    hasMinuteError ||
    selectedStatuses.length === 0 ||
    selectedStatuses.some((x) => x.hasWarning) ||
    selectedInactiveStatus?.hasWarning;

  return (
    <>
      <ConfirmAutomaticStatusChangeModal
        isOpen={isPendingConfirm}
        statusIds={selectedStatusIds}
        onConfirm={onConfirmBtnClick}
        onClose={onConfirmCancelBtnClick}
        inactivityDurationUnit={timeUnit}
        inactivityDurationValue={timeValue}
      />

      <CModal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <CModalHeader>{ruleForEdit ? 'Edit' : 'Add'} Automatic Status Change</CModalHeader>
          <ModalCloseButton />
          <CModalBody>
            <span>After</span>
            <TimeInput
              value={isNaN(timeValue) ? '' : timeValue}
              onChange={onTimeInputChanged}
              min={timeUnit === ChannelStatusInactivityUnits.Minutes ? 5 : 1}
              onBlur={onTimeInputBlur}
            >
              <NumberField />
            </TimeInput>
            <InlineDropdown
              value={timeUnit}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => onTimeUnitChanged(e.currentTarget.value)}
              display="inline-flex"
              width="unset"
            >
              <option value={ChannelStatusInactivityUnits.Minutes}>{ChannelStatusInactivityUnits.Minutes}</option>
              <option value={ChannelStatusInactivityUnits.Hours}>{ChannelStatusInactivityUnits.Hours}</option>
              <option value={ChannelStatusInactivityUnits.Days}>{ChannelStatusInactivityUnits.Days}</option>
            </InlineDropdown>
            <Text as="span">of inactivity, change conversations with status:</Text>
            <VStack alignItems="flex-start">
              <InlineDropdown
                value="PlaceHolder"
                onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => onAddInitialStatus(e.currentTarget.value)}
                display="inline-flex"
                width="unset"
              >
                {selectableStatusesDisplay.map(({ label, value }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </InlineDropdown>
              <Wrap>
                {selectedStatuses.map((status) => (
                  <Tag
                    key={status.id}
                    label={status.name}
                    {...(status.hasWarning && {
                      leftElement: (
                        <Tooltip
                          content={'The status with this id no longer exists. Please remove this status and save.'}
                        >
                          <Icon name="warningSign" />
                        </Tooltip>
                      ),
                    })}
                    color="text.onContrast"
                    bg={status.color}
                    closeButtonDisplay="always"
                    onClose={(): void => onRemoveInitialStatusClick(status.id)}
                  />
                ))}
              </Wrap>
            </VStack>

            <Text mb="0.5rem">to</Text>

            <HStack>
              {selectedInactiveStatus?.hasWarning && (
                <Tooltip content={'The saved inactive status no longer exists. Please update this status and save.'}>
                  <Icon name="warningSign" />
                </Tooltip>
              )}
              <InlineDropdown
                value={selectedInactiveStatusId}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>): void =>
                  onInactiveStatusSelectionChanged(e.currentTarget.value)
                }
                placeholder="Select..."
                display="inline-flex"
                width="unset"
              >
                {selectableInactiveStatusOptions.map((status) => {
                  return (
                    <option key={status.id} value={status.id}>
                      {status.name}
                    </option>
                  );
                })}
              </InlineDropdown>
            </HStack>

            {hasMinuteError && <ErrorMessage>The inactivity duration must be at least 5 minutes </ErrorMessage>}
          </CModalBody>

          <CModalFooter justifyContent="space-between">
            <Button variant="tertiary" onClick={onClose}>
              Close
            </Button>
            <Button isLoading={isSaving} disabled={isSaveBtnDisabled} onClick={onSaveBtnClick}>
              Save
            </Button>
          </CModalFooter>
        </ModalContent>
      </CModal>
    </>
  );
};

export default AddEditAutomaticStatusChangeModal;

const NumberField = styled(NumberInputField)`
  && {
    border-radius: 6px;
    height: 1.5rem;
    padding: 0;
    text-align: center;
    width: 3rem;
  }
`;

const TimeInput = styled(NumberInput)`
  margin: 0 0.5rem;
  display: inline-flex;
`;

const InlineDropdown = styled(CSelect)`
  && {
    border-radius: 6px;
    display: inline-flex;
    height: 1.5rem;
    margin: 0 0.5rem 0 0;
  }
`;

const ErrorMessage = styled.p`
  color: red;
  font-size: 14px;
  margin-top: 1rem;
`;
